// ============================================
// TERMS OF SERVICE PAGE
// ============================================

import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle, Button } from '../../components/ui';
import { APP_NAME, COMPANY_FULL_NAME, SERVICE_FEE, ROUTES } from '../../constants';
import styles from './Legal.module.css';

import { useEffect } from 'react';
// ...existing imports...

export function TermsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  return (
    <Layout maxWidth="md">
      <div className={styles.container}>
        <Link to={ROUTES.HOME} className={styles.backLink}>
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        <Card variant="elevated">
          <CardHeader>
            <div className={styles.headerIcon}>
              <FileText size={32} />
            </div>
            <CardTitle>Terms of Service</CardTitle>
            <p className={styles.lastUpdated}>Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
          </CardHeader>

          <CardContent>
            <div className={styles.content}>
              
              <section className={styles.section}>
                <h2>1. Service Overview</h2>
                <p>
                  <strong>{COMPANY_FULL_NAME}</strong> (referred to as "{APP_NAME}", "we", "us", or "our") 
                  provides a personalized device recommendation service. By using our service and paying the 
                  consultation fee of <strong>9{SERVICE_FEE}</strong>, you agree to these terms.
                </p>
              </section>

              <section className={styles.highlightSection}>
                <div className={styles.highlightHeader}>
                  <AlertCircle size={20} />
                  <h3>Important: What This Service Is</h3>
                </div>
                <ul className={styles.checkList}>
                  <li>
                    <CheckCircle size={16} className={styles.checkIcon} />
                    <span>We provide <strong>expert suggestions and recommendations only</strong></span>
                  </li>
                  <li>
                    <CheckCircle size={16} className={styles.checkIcon} />
                    <span>Recommendations are based on information you provide to us</span>
                  </li>
                  <li>
                    <CheckCircle size={16} className={styles.checkIcon} />
                    <span>Our team has <strong>20+ years of industry experience</strong></span>
                  </li>
                  <li>
                    <CheckCircle size={16} className={styles.checkIcon} />
                    <span>We guide you to make an informed purchase decision</span>
                  </li>
                </ul>
              </section>

              <section className={styles.warningSection}>
                <div className={styles.warningHeader}>
                  <AlertCircle size={20} />
                  <h3>What This Service Is NOT</h3>
                </div>
                <ul className={styles.crossList}>
                  <li>We do NOT sell, supply, or deliver any laptops, desktops, or hardware</li>
                  <li>We do NOT provide installation, repair, or maintenance services</li>
                  <li>We do NOT guarantee product availability or pricing from vendors</li>
                  <li>We do NOT act as intermediaries in any purchase transaction</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>2. Non-Refundable Payment</h2>
                <div className={styles.importantBox}>
                  <p>
                    <strong>The consultation fee of ₹{SERVICE_FEE} is strictly non-refundable.</strong>
                  </p>
                  <p>
                    Once you submit payment, our team begins reviewing your requirements and preparing 
                    your personalized recommendation. This effort cannot be reversed, hence no refunds 
                    will be processed under any circumstances.
                  </p>
                </div>
                <p>By completing payment, you acknowledge and accept:</p>
                <ul>
                  <li>The fee covers our expert consultation time and effort</li>
                  <li>Recommendations will be delivered as described</li>
                  <li>No refund requests will be entertained</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>3. Recommendation Delivery</h2>
                <p>
                  After successful payment, our experts will analyze your requirements and provide 
                  personalized recommendations through <strong>WhatsApp</strong> using the contact 
                  number you provided during registration.
                </p>
                <p>
                  Recommendations typically include:
                </p>
                <ul>
                  <li>Specific product recommendations matching your needs</li>
                  <li>Budget-appropriate options</li>
                  <li>Key specifications to look for</li>
                  <li>Buying tips and considerations</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>4. Your Responsibilities</h2>
                <ul>
                  <li>Provide accurate and complete information in the form</li>
                  <li>Ensure your WhatsApp number is active and correct</li>
                  <li>Respond to any clarification requests from our team</li>
                  <li>Make your own final purchase decisions</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>5. Limitation of Liability</h2>
                <p>
                  Our recommendations are suggestions based on your stated requirements and our expertise. 
                  We are not liable for:
                </p>
                <ul>
                  <li>Your final purchase decisions</li>
                  <li>Product defects, issues, or dissatisfaction with purchased items</li>
                  <li>Price changes, availability, or vendor-related issues</li>
                  <li>Any indirect or consequential damages</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>6. Contact Us</h2>
                <p>
                  For any questions about these terms, please contact us through our website or 
                  WhatsApp support.
                </p>
              </section>

            </div>
          </CardContent>
        </Card>

        <div className={styles.footer}>
          <Button variant="outline" onClick={() => window.history.back()}>
            <ArrowLeft size={18} />
            Go Back
          </Button>
        </div>
      </div>
    </Layout>
  );
}
